import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import { Box, Grid, Stack } from '@mui/material';
import { Form, Formik } from 'formik';

import useNotification from '../../../../core/hooks/use_notification';
import { MISC_DATE_ISO_FORMAT, ROUTE_PODS } from '../../../../core/constants';
import TextFieldFormik from '../../../common/text_field';
import { SubmitButton } from '../../../common/button';
import { getAllPodsState } from '../../../../features/pod/get_all_pods';
import { parseDateString } from '../../../../core/utils/date_utils';
import { DateTimePickerFormik } from '../../../common/date_picker';
import RadioGroupFormik from '../../../common/radio_group';
import { yesNoOptions } from '../../../common/form_utils';
import { SelectFieldFormik } from '../../../common/select_field';
import { getEventRegisterReasonOptions } from '../../../common/utils/event_register_utils';
import { createEventRegisterAction, createEventRegisterState } from '../../../../features/pod/create_event_register';
import { createEventRegisterNotificationData } from '../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import PageHeader from '../../../common/page_header';
import SectionPaper from '../../../common/section_paper';
import BottomActionBar from '../../../common/bottom_action_bar';

const EventRegisterForm = () => {
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const getAllPods = useSelector(getAllPodsState);
    const createEventRegister = useSelector(createEventRegisterState);

    const initialValues = {
        fromDate: '',
        toDate: '',
        description: '',
        omitInReports: false,
        podId: '',
        reason: '',
    };

    const schema = Yup.object().shape({
        fromDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required(),
        toDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required(),
        description: Yup.string().notRequired(),
        omitInReports: Yup.boolean().notRequired(),
        podId: Yup.string().required(),
        reason: Yup.string().required(),
    });

    const onSubmit = (values) => {
        const fromDate = values?.fromDate?.format(MISC_DATE_ISO_FORMAT);
        const toDate = values?.toDate?.format(MISC_DATE_ISO_FORMAT);

        const form = {
            ...values,
            fromDate,
            toDate
        };

        dispatch(createEventRegisterAction(form))
            .then(response => {
                showNotification(createEventRegisterNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(ROUTE_PODS, { state: { tab: location?.state?.tab } });
            });
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <PageHeader
                title="Nowe zdarzenie"
                showBack={ true }
                onBack={ () => navigate(ROUTE_PODS, { state: { tab: location?.state?.tab } }) }
            />

            <Formik
                initialValues={ initialValues }
                validationSchema={ schema }
                validateOnChange={ false }
                validateOnBlur={ false }
                onSubmit={ onSubmit }>
                { () => (
                    <Box component={ Form }
                         sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                        <Box sx={ { flexGrow: 1, mb: 4 } }>
                            <Stack spacing={ 3 }>
                                <SectionPaper title="Podstawowe Informacje">
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <DateTimePickerFormik
                                                name="fromDate"
                                                label="Data od"
                                                required
                                                InputLabelProps={ { shrink: true } }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <DateTimePickerFormik
                                                name="toDate"
                                                label="Data do"
                                                required
                                                InputLabelProps={ { shrink: true } }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <SelectFieldFormik
                                                name="podId"
                                                label="Punkt Sprzedaży (Pod)"
                                                options={ getAllPods?.data?.map(pod => ({
                                                    value: pod?.id,
                                                    label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + (pod?.device?.serviceId || 'brak urządzenia')
                                                })) }
                                                required
                                            />
                                        </Grid>
                                    </Grid>
                                </SectionPaper>

                                <SectionPaper title="Szczegóły Zdarzenia">
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 } md={ 4 }>
                                            <SelectFieldFormik
                                                name="reason"
                                                label="Powód"
                                                options={ getEventRegisterReasonOptions }
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 } md={ 8 }>
                                            <RadioGroupFormik
                                                name="omitInReports"
                                                label="Pominąć w raportach"
                                                options={ yesNoOptions }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <TextFieldFormik name="description" label="Opis zdarzenia" multiline
                                                             rows={ 4 }/>
                                        </Grid>
                                    </Grid>
                                </SectionPaper>
                            </Stack>
                        </Box>

                        <BottomActionBar>
                            <SubmitButton isLoading={ createEventRegister?.loading }>
                                Zapisz
                            </SubmitButton>
                        </BottomActionBar>

                    </Box>
                ) }
            </Formik>
        </Box>
    );
};

export default EventRegisterForm;